import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  ButtonGroup,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  Radio,
  RadioGroup,
  Switch,
  TextField,
  Tooltip,
  Typography
} from "@mui/material";
import { useConfirm } from "material-ui-confirm";
import { FieldArray, Formik } from "formik";
import { ConnectedFocusError } from "focus-formik-error";
import { DragDropContext, Draggable, Droppable } from "@hello-pangea/dnd";
import TimerPicker from "../../../../../components/utilities/TimerPicker";
import EventTypeCard from "../../SubComponents/EventTypeCard";
import HelpButton from "../../../../../components/utilities/HelpButton";
import {
  BCPAngleDown,
  BCPCircleCheck,
  BCPCircleQuestion,
  BCPEye,
  BCPGear,
  BCPGripLines
} from "../../../../../components/icons/bcp/BCPIcons";
import { getEnvironmentVariable } from "../../../../../global/environmentVariables";
import ScorecardPicker from "../../../../../components/utilities/ScorecardPicker";
import GenericPicker from "../../../../../components/utilities/GenericPicker";
import { useContext } from "react";
import { AuthContext } from "../../../../../components/user/BCPAuthProvider";
import { BetaPill } from "../../../../../components/utilities/BetaFeaturePopup";
const IS_UY = getEnvironmentVariable("IS_UY") === "true";

export default function EventCreationGameConfiguration({
  getInitialValues,
  getConfigValidationSchema,
  submitConfig,
  setEventValues,
  event,
  copyEvent,
  eventId,
  eventValues,
  gameSystem,
  scorecards,
  scorecardDict,
  teamScorecards,
  scorecardAdvancedOpen,
  setScorecardAdvancedOpen,
  theme,
  isCustom,
  setIsCustom,
  readOnly,
  onDragEnd,
  ScorecardAdvancedModal,
  templates,
  TemplateModal,
  tempModalOpen,
  setTempModalOpen,
  template,
  setTemplate,
  templateChangesPrompt,
  EventStepper,
  step,
  setStep,
  scorecardModalOpen,
  setScorecardModalOpen,
  ScorecardModal,
  circuits
}) {
  const authContext = useContext(AuthContext);

  const placingRecordTypes =
    event?.gameSystem?.placingRecordTypes ||
    gameSystem?.placingRecordTypes ||
    [];
  const confirm = useConfirm();

  return (
    <Grid item xs={12} sx={{ pt: 2 }}>
      <Formik
        initialValues={getInitialValues()}
        validationSchema={getConfigValidationSchema()}
        onSubmit={submitConfig}
      >
        {({
          errors,
          handleBlur,
          handleChange,
          handleSubmit,
          touched,
          values,
          setFieldValue,
          setFieldTouched
        }) => (
          <form onSubmit={handleSubmit}>
            {/* Event Creation Progress Stepper */}
            <EventStepper
              step={step}
              setStep={setStep}
              event={event}
              circuits={circuits}
            />

            <ConnectedFocusError />

            <Grid
              container
              spacing={2}
              pt={1}
              onBlur={() => {
                setEventValues(
                  eventValues ? { ...eventValues, ...values } : { ...values }
                );
              }}
            >
              {templates && templates?.length && !eventId ? (
                <>
                  <Grid item xs={12}>
                    <Divider
                      sx={{
                        mt: 1,
                        mb: 1
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControl>
                      <Typography variant="h4" fontWeight="700">
                        Event Templates
                      </Typography>
                      <RadioGroup
                        defaultValue={values?.templateId || "default"}
                      >
                        <Grid container sx={{ pt: 2 }}>
                          {templates.map((temp, i) => (
                            <>
                              {(i === 0 ||
                                temp.templateType?.label !==
                                  templates[i - 1].templateType?.label) && (
                                <Grid item xs={12}>
                                  <Typography variant="h5" fontWeight="700">
                                    {temp.templateType?.label || "Other"}
                                  </Typography>
                                </Grid>
                              )}
                              <Grid item xs={12}>
                                <FormControlLabel
                                  value={temp?.id}
                                  control={<Radio />}
                                  onChange={(e) => {
                                    setFieldValue(
                                      "pairingMetrics",
                                      temp.pairingMetrics
                                    );
                                    setFieldValue(
                                      "placingMetrics",
                                      temp.placingMetrics
                                    );

                                    if (temp?.id !== "custom") {
                                      setFieldValue("templateName", temp.name);
                                      setFieldValue("templateId", temp.id);
                                    }

                                    if (
                                      (!temp?.scorecard?.id ||
                                        temp?.scorecard?.id === "") &&
                                      scorecards?.length
                                    ) {
                                      scorecards.forEach((sc, i) => {
                                        if (
                                          sc?.label === temp?.scorecard?.name
                                        ) {
                                          setFieldValue(
                                            "scorecardId",
                                            sc?.value
                                          );
                                        }
                                      });
                                    } else {
                                      setFieldValue(
                                        "scorecardId",
                                        temp?.scorecard?.id
                                      );
                                    }

                                    if (temp?.playerPlacingMetrics) {
                                      setFieldValue(
                                        "playerPlacingMetrics",
                                        temp.playerPlacingMetrics
                                      );
                                    }

                                    if (temp?.id === "custom") {
                                      setIsCustom(true);
                                      setFieldValue("templateModified", true);
                                    } else {
                                      setFieldValue("templateModified", false);
                                    }
                                  }}
                                  checked={
                                    (values?.templateId === temp?.id &&
                                      !values?.templateModified) ||
                                    (temp?.id === "custom" &&
                                      values?.templateModified)
                                  }
                                  label={
                                    <Typography className="MuiFormControlLabel-label">
                                      {temp?.name}
                                      <IconButton
                                        onClick={() => {
                                          setTemplate(temp);
                                          setTempModalOpen(true);
                                        }}
                                        sx={{ ml: "8px" }}
                                      >
                                        <BCPCircleQuestion />
                                      </IconButton>
                                    </Typography>
                                  }
                                />
                              </Grid>
                            </>
                          ))}
                        </Grid>
                      </RadioGroup>
                    </FormControl>
                  </Grid>
                </>
              ) : (
                <></>
              )}

              <Grid item xs={12}>
                <Divider
                  sx={{
                    mt: 1,
                    mb: 1
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="h4" fontWeight="700">
                  Round Settings
                </Typography>
                <Typography variant="caption" gutterBottom>
                  These can be edited before and during the event.
                </Typography>
              </Grid>

              {values.pairingStyle?.toLowerCase() === "swiss" &&
                !event?.gameSystem?.roundsLogBase2 &&
                !gameSystem?.roundsLogBase2 && (
                  <Grid item xs={6}>
                    <TextField
                      name="numberOfRounds"
                      label="Number of Rounds"
                      value={values.numberOfRounds}
                      variant="outlined"
                      fullWidth
                      required
                      onBlur={handleBlur}
                      onChange={handleChange}
                      error={Boolean(
                        touched.numberOfRounds && errors.numberOfRounds
                      )}
                      helperText={
                        touched.numberOfRounds && errors.numberOfRounds
                      }
                      disabled={readOnly}
                      type="number"
                      inputProps={{
                        min: 0
                      }}
                    />
                  </Grid>
                )}
              <Grid
                item
                xs={
                  values.pairingStyle?.toLowerCase() === "swiss" &&
                  !event?.gameSystem?.roundsLogBase2 &&
                  !gameSystem?.roundsLogBase2
                    ? 6
                    : 12
                }
              >
                <TimerPicker
                  label="Default Round Length"
                  name="defaultRoundLength"
                  value={values.defaultRoundLength}
                  setFieldValue={setFieldValue}
                  disabled={readOnly}
                />
              </Grid>

              {(authContext?.authState?.identity?.betaUser === "true" ||
                authContext?.authState?.identity?.isAdmin ||
                authContext?.authState?.identity?.adminGameSystems?.length >
                  0) && (
                <Grid item xs={12}>
                  <FormControlLabel
                    label={
                      <Box sx={{ lineHeight: "16px" }}>
                        <Typography className="MuiFormControlLabel-label">
                          Require Pairings to be Published <BetaPill />
                        </Typography>
                        <Typography variant="caption">
                          Enabling this will add an additional step for
                          generating pairings. Pairings will not be visible to
                          players until after you publish them each round.
                        </Typography>
                      </Box>
                    }
                    sx={{
                      ml: 0,
                      width: "100%",
                      "& .MuiFormControlLabel-label": {
                        width: "calc(100% - 58px)"
                      }
                    }}
                    control={
                      <Switch
                        color="primary"
                        checked={values.requirePairingPublish}
                        onChange={(e) => {
                          setFieldValue(
                            "requirePairingPublish",
                            !values.requirePairingPublish
                          );
                        }}
                        disabled={readOnly}
                      />
                    }
                  />
                </Grid>
              )}

              <Grid item xs={12}>
                <Divider
                  sx={{
                    mt: 1,
                    mb: 1
                  }}
                />
              </Grid>

              {((event && event?.gameSystem?.allowsTeamEvent) ||
                gameSystem?.allowsTeamEvent) && (
                <>
                  <Grid item xs={12}>
                    <Typography variant="h4" fontWeight="700">
                      Event Type
                    </Typography>
                    <Typography variant="caption" gutterBottom>
                      This cannot be changed after event is created.
                    </Typography>
                  </Grid>

                  <Grid item xs={12}>
                    <EventTypeCard
                      values={values}
                      setFieldValue={setFieldValue}
                      disabled={Boolean(eventId) || readOnly}
                      event={event}
                      gameSystem={gameSystem}
                    />
                  </Grid>

                  {values?.eventType === "Teams Event" && (
                    <>
                      <Grid item xs={12}>
                        <ScorecardPicker
                          name="teamScorecardId"
                          label="Team Scorecard"
                          value={values.teamScorecardId}
                          handleChange={(e) => {
                            if (
                              scorecardDict?.[e.target.value]?.variables
                                ?.length > 0
                            ) {
                              let variables =
                                scorecardDict[e.target.value].variables;
                              for (let variable of variables) {
                                if (values[variable.id] === undefined) {
                                  setFieldValue(
                                    variable.id,
                                    variable.default !== undefined
                                      ? variable.default
                                      : 0
                                  );
                                }
                              }
                            }

                            handleChange(e);
                          }}
                          scorecards={teamScorecards}
                          inclAny={false}
                          disabled={readOnly}
                          endAdornment={
                            <InputAdornment position="end">
                              <HelpButton
                                title={
                                  !scorecardDict?.[values.teamScorecardId]
                                    ? "Please select a Team Scorecard"
                                    : scorecardDict[values.teamScorecardId]
                                        .description ||
                                      "The scorecard used to determine a Team Match Win."
                                }
                              />
                            </InputAdornment>
                          }
                          error={Boolean(
                            touched.teamScorecardId && errors.teamScorecardId
                          )}
                          helperText={
                            touched.teamScorecardId && errors.teamScorecardId
                          }
                        />
                      </Grid>

                      {scorecardDict?.[values.teamScorecardId]?.variables
                        ?.length > 0 &&
                        scorecardDict[values.teamScorecardId].variables.map(
                          (variable, index) => (
                            <Grid key={index} item xs={12}>
                              <TextField
                                name={variable.id}
                                label={variable.label}
                                value={values[variable.id]}
                                variant="outlined"
                                fullWidth
                                type={variable.type || "number"}
                                onBlur={handleBlur}
                                onChange={handleChange}
                                InputProps={{
                                  sx: {
                                    cursor: "pointer",
                                    "& input": {
                                      cursor: "pointer"
                                    }
                                  },
                                  endAdornment: variable.description ? (
                                    <InputAdornment position="end">
                                      <HelpButton
                                        title={variable.description}
                                      />
                                    </InputAdornment>
                                  ) : null
                                }}
                                disabled={readOnly}
                              />
                            </Grid>
                          )
                        )}
                    </>
                  )}

                  <Grid item xs={12}>
                    <Divider
                      sx={{
                        mt: 1,
                        mb: 1
                      }}
                    />
                  </Grid>
                </>
              )}

              <Grid item xs={12}>
                <Grid item xs={12}>
                  <FormControlLabel
                    label={
                      <Box sx={{ lineHeight: "16px" }}>
                        <Typography className="MuiFormControlLabel-label">
                          Hide{" "}
                          {event?.gameSystem?.listLabel ||
                            copyEvent?.gameSystem?.listLabel ||
                            gameSystem?.listLabel ||
                            "List"}
                          {"s "}
                        </Typography>
                        <Typography variant="caption">
                          {`Enabling this will hide ${
                            event?.gameSystem?.listLabel ||
                            copyEvent?.gameSystem?.listLabel ||
                            gameSystem?.listLabel ||
                            "List"
                          }s from players until the event has ended, or you disable this feature.`}
                        </Typography>
                      </Box>
                    }
                    sx={{
                      ml: 0,
                      width: "100%",
                      "& .MuiFormControlLabel-label": {
                        width: "calc(100% - 58px)"
                      }
                    }}
                    control={
                      <Switch
                        color="primary"
                        checked={values.hideLists}
                        onChange={(e) => {
                          setFieldValue("hideLists", !values.hideLists);
                        }}
                        disabled={readOnly}
                      />
                    }
                  />
                </Grid>
              </Grid>

              <Grid item xs={12}>
                <Divider
                  sx={{
                    mt: 1,
                    mb: 1
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="h4" fontWeight="700">
                  Additional Game Settings{" "}
                  {!eventId && (
                    <HelpButton title="Additional game settings include table configuration, self-scoring settings, showing/hiding roster information, list settings, and customizing scoring metrics." />
                  )}
                </Typography>
              </Grid>

              {!eventId && (
                <>
                  <Grid item xs={12}>
                    <ButtonGroup fullWidth disabled={readOnly}>
                      <Button
                        variant={isCustom ? "outlined" : "contained"}
                        onClick={() => {
                          setIsCustom(false);
                        }}
                        startIcon={!isCustom && <BCPCircleCheck />}
                      >
                        Use Recommended
                      </Button>
                      <Button
                        variant={isCustom ? "contained" : "outlined"}
                        onClick={() => {
                          setIsCustom(true);
                        }}
                        startIcon={isCustom && <BCPCircleCheck />}
                      >
                        Custom
                      </Button>
                    </ButtonGroup>
                  </Grid>
                  <Grid item xs={12} mt={2}>
                    <Typography variant="body1" gutterBottom>
                      {`"Use recommended" will always abide by the game's default settings.`}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="body1" gutterBottom>
                      {`Soft Scoring and Hobby Scoring can be set up in your event details once you save this event.`}
                    </Typography>
                  </Grid>
                </>
              )}

              {(isCustom || values?.templateModified || eventId) && (
                <>
                  {!eventId && (
                    <>
                      <Grid item xs={12}>
                        <Divider
                          sx={{
                            mt: 1,
                            mb: 1
                          }}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="h3" fontWeight="700">
                          Additional Game Settings
                        </Typography>
                      </Grid>
                    </>
                  )}

                  {!event?.gameSystem?.hideRankedTables &&
                    !gameSystem?.hideRankedTables && (
                      <>
                        <Grid item xs={12}>
                          <ButtonGroup fullWidth disabled={readOnly}>
                            <Button
                              variant={
                                values?.rankedTables ? "outlined" : "contained"
                              }
                              onClick={() => {
                                setFieldValue("rankedTables", false);
                              }}
                              startIcon={
                                !values?.rankedTables && <BCPCircleCheck />
                              }
                            >
                              Random Tables
                            </Button>
                            <Button
                              variant={
                                values?.rankedTables ? "contained" : "outlined"
                              }
                              onClick={() => {
                                setFieldValue("rankedTables", true);
                              }}
                              startIcon={
                                values?.rankedTables && <BCPCircleCheck />
                              }
                            >
                              Ranked Tables
                            </Button>
                          </ButtonGroup>
                        </Grid>

                        <Grid item xs={12} sx={{ mt: -1 }}>
                          <Typography variant="caption">
                            {values?.rankedTables
                              ? "Put top ranked players on the lowest numbered tables. You can change this mid-tournament."
                              : "Put players on random tables, ignore placing information. You can change this mid-tournament."}
                          </Typography>
                        </Grid>
                      </>
                    )}

                  <Grid item xs={12}>
                    <TextField
                      name="startingTable"
                      label="Starting Table"
                      value={values.startingTable}
                      variant="outlined"
                      fullWidth
                      type="number"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      error={Boolean(
                        touched.startingTable && errors.startingTable
                      )}
                      helperText={touched.startingTable && errors.startingTable}
                      InputProps={{
                        sx: {
                          cursor: "pointer",
                          "& input": {
                            cursor: "pointer"
                          }
                        },
                        endAdornment: (
                          <InputAdornment position="end">
                            <HelpButton title="This option is to determine the table number of your lowest numbered table. For example if you want to start players on table 30 and go up from there, set your starting table to 30." />
                          </InputAdornment>
                        )
                      }}
                      disabled={readOnly}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Divider
                      sx={{
                        mt: 1,
                        mb: 1
                      }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <FormControlLabel
                      label={
                        <Box sx={{ lineHeight: "16px" }}>
                          <Typography
                            variant="body1"
                            className="MuiFormControlLabel-label"
                          >
                            Self Scoring{" "}
                          </Typography>
                          <Typography variant="caption">
                            {`Enabling this will allow players to submit their own scores via the player app.`}
                          </Typography>
                        </Box>
                      }
                      sx={{
                        ml: 0,
                        width: "100%",
                        "& .MuiFormControlLabel-label": {
                          width: "calc(100% - 58px)"
                        }
                      }}
                      control={
                        <Switch
                          color="primary"
                          checked={values.enablePasswords}
                          onChange={(e) => {
                            setFieldValue(
                              "enablePasswords",
                              !values.enablePasswords
                            );
                          }}
                          disabled={readOnly}
                        />
                      }
                    />
                  </Grid>

                  {!IS_UY && (
                    <Grid item xs={12}>
                      <FormControlLabel
                        label={
                          <Box sx={{ lineHeight: "16px" }}>
                            <Typography
                              variant="body1"
                              className="MuiFormControlLabel-label"
                            >
                              Require Self-Score PIN
                            </Typography>
                            <Typography variant="caption">
                              {`Players can only submit their score if they enter the PIN they receieve at registration.`}
                            </Typography>
                          </Box>
                        }
                        sx={{
                          ml: 0,
                          width: "100%",
                          "& .MuiFormControlLabel-label": {
                            width: "calc(100% - 58px)"
                          }
                        }}
                        control={
                          <Switch
                            color="primary"
                            checked={!values.passwordlessScoring}
                            onChange={(e) => {
                              setFieldValue(
                                "passwordlessScoring",
                                !values.passwordlessScoring
                              );
                            }}
                            disabled={readOnly || !values.enablePasswords}
                          />
                        }
                      />
                    </Grid>
                  )}

                  <Grid item xs={12}>
                    <Divider
                      sx={{
                        mt: 1,
                        mb: 1
                      }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <FormControlLabel
                      label="Hide Roster"
                      sx={{
                        ml: 0,
                        width: "100%",
                        "& .MuiFormControlLabel-label": {
                          width: "calc(100% - 58px)"
                        }
                      }}
                      control={
                        <Switch
                          color="primary"
                          checked={values.hideRoster}
                          onChange={async (e) => {
                            try {
                              if (!values.hideRoster && !values.hidePlacings) {
                                await confirm({
                                  title: "Hide Roster & Placings?",
                                  description:
                                    "Hiding the roster will also hide placings. Are you sure you want to hide both?",
                                  dialogProps: {
                                    sx: {
                                      zIndex: 99999
                                    }
                                  }
                                });

                                setFieldValue("hideRoster", true);
                                setFieldValue("hidePlacings", true);
                              } else {
                                setFieldValue("hideRoster", !values.hideRoster);
                              }
                            } catch (err) {
                              return;
                            }
                          }}
                          disabled={readOnly}
                        />
                      }
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControlLabel
                      label="Hide Placings"
                      sx={{
                        ml: 0,
                        width: "100%",
                        "& .MuiFormControlLabel-label": {
                          width: "calc(100% - 58px)"
                        }
                      }}
                      control={
                        <Switch
                          color="primary"
                          checked={values.hidePlacings}
                          onChange={async (e) => {
                            try {
                              if (values.hideRoster) {
                                await confirm({
                                  title: "Unhide Placings & Roster?",
                                  description: `Unhiding placings will also unhide the roster. Are you sure you want to unhide both?`,
                                  dialogProps: {
                                    sx: {
                                      zIndex: 99999
                                    }
                                  }
                                });

                                setFieldValue("hideRoster", false);
                                setFieldValue("hidePlacings", false);
                              } else {
                                setFieldValue(
                                  "hidePlacings",
                                  !values.hidePlacings
                                );
                              }
                            } catch (err) {
                              return;
                            }
                          }}
                          disabled={readOnly}
                        />
                      }
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <FormControlLabel
                      label="Hide Player Count"
                      sx={{
                        ml: 0,
                        width: "100%",
                        "& .MuiFormControlLabel-label": {
                          width: "calc(100% - 58px)"
                        }
                      }}
                      control={
                        <Switch
                          color="primary"
                          checked={values.hidePlayerCount}
                          onChange={(e) => {
                            setFieldValue(
                              "hidePlayerCount",
                              !values.hidePlayerCount
                            );
                          }}
                          disabled={readOnly}
                        />
                      }
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Divider
                      sx={{
                        mt: 1,
                        mb: 1
                      }}
                    />
                  </Grid>

                  {!(
                    event?.gameSystem?.textOnlyLists ||
                    gameSystem?.textOnlyLists
                  ) && (
                    <Grid item xs={12}>
                      <FormControlLabel
                        label={
                          <Box sx={{ lineHeight: "16px" }}>
                            <Typography
                              variant="body1"
                              className="MuiFormControlLabel-label"
                            >
                              {`Enforce Text-Only ${
                                event?.gameSystem?.listLabel ||
                                copyEvent?.gameSystem?.listLabel ||
                                gameSystem?.listLabel ||
                                "List"
                              }s`}
                            </Typography>
                            <Typography variant="caption">
                              {`Enabling this will only allow players to submit text-based ${event?.gameSystem?.listLabel || copyEvent?.gameSystem?.listLabel || gameSystem?.listLabel || "list"}s and disable all other submission types.`}
                            </Typography>
                          </Box>
                        }
                        sx={{
                          ml: 0,
                          width: "100%",
                          "& .MuiFormControlLabel-label": {
                            width: "calc(100% - 58px)"
                          }
                        }}
                        control={
                          <Switch
                            color="primary"
                            checked={values.allowTextOnly}
                            onChange={(e) => {
                              if (values.allowTextOnly) {
                                setFieldValue("listOptions.allowsFiles", true);
                                setFieldValue("listOptions.allowsImages", true);
                              } else {
                                setFieldValue("listOptions.allowsFiles", false);
                                setFieldValue(
                                  "listOptions.allowsImages",
                                  false
                                );
                              }
                              setFieldValue(
                                "allowTextOnly",
                                !values.allowTextOnly
                              );
                            }}
                            disabled={readOnly}
                          />
                        }
                      />
                    </Grid>
                  )}

                  <Grid item xs={12}>
                    <FormControlLabel
                      label={
                        <Box sx={{ lineHeight: "16px" }}>
                          <Typography
                            variant="body1"
                            className="MuiFormControlLabel-label"
                          >
                            {event?.gameSystem?.listLabel ||
                              copyEvent?.gameSystem?.listLabel ||
                              gameSystem?.listLabel ||
                              "List"}{" "}
                            Required at Checkin{" "}
                          </Typography>
                          <Typography variant="caption">
                            {`Enabling this will not allow a player to check-in without first submitting a ${
                              event?.gameSystem?.listLabel ||
                              copyEvent?.gameSystem?.listLabel ||
                              gameSystem?.listLabel ||
                              "list"
                            }.`}
                          </Typography>
                        </Box>
                      }
                      sx={{
                        ml: 0,
                        width: "100%",
                        "& .MuiFormControlLabel-label": {
                          width: "calc(100% - 58px)"
                        }
                      }}
                      control={
                        <Switch
                          color="primary"
                          checked={values.listsAtCheckin}
                          onChange={(e) => {
                            setFieldValue(
                              "listsAtCheckin",
                              !values.listsAtCheckin
                            );
                          }}
                          disabled={readOnly}
                        />
                      }
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <FormControlLabel
                      label={
                        <Box sx={{ lineHeight: "16px" }}>
                          <Typography
                            variant="body1"
                            className="MuiFormControlLabel-label"
                          >
                            Disable{" "}
                            {event?.gameSystem?.listLabel ||
                              copyEvent?.gameSystem?.listLabel ||
                              gameSystem?.listLabel ||
                              "List"}{" "}
                            Editing{" "}
                          </Typography>
                          <Typography variant="caption">
                            {`Once a ${
                              event?.gameSystem?.listLabel ||
                              copyEvent?.gameSystem?.listLabel ||
                              gameSystem?.listLabel ||
                              "list"
                            } is submitted, players cannot edit them.`}
                          </Typography>
                        </Box>
                      }
                      sx={{
                        ml: 0,
                        width: "100%",
                        "& .MuiFormControlLabel-label": {
                          width: "calc(100% - 58px)"
                        }
                      }}
                      control={
                        <Switch
                          color="primary"
                          checked={values.listsLocked}
                          onChange={(e) => {
                            setFieldValue("listsLocked", !values.listsLocked);
                          }}
                          disabled={readOnly}
                        />
                      }
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <FormControlLabel
                      label={
                        <Box sx={{ lineHeight: "16px" }}>
                          <Typography
                            variant="body1"
                            className="MuiFormControlLabel-label"
                          >
                            Disable{" "}
                            {event?.gameSystem?.listLabel ||
                              copyEvent?.gameSystem?.listLabel ||
                              gameSystem?.listLabel ||
                              "List"}{" "}
                            Submission{" "}
                          </Typography>
                          <Typography variant="caption">
                            {`If enabled, players will not be allowed to submit their ${
                              event?.gameSystem?.listLabel ||
                              copyEvent?.gameSystem?.listLabel ||
                              gameSystem?.listLabel ||
                              "list"
                            }s anymore.`}
                          </Typography>
                        </Box>
                      }
                      sx={{
                        ml: 0,
                        width: "100%",
                        "& .MuiFormControlLabel-label": {
                          width: "calc(100% - 58px)"
                        }
                      }}
                      control={
                        <Switch
                          color="primary"
                          checked={values.listSubmissionLocked}
                          onChange={(e) => {
                            setFieldValue(
                              "listSubmissionLocked",
                              !values.listSubmissionLocked
                            );
                          }}
                          disabled={readOnly}
                        />
                      }
                    />
                  </Grid>

                  {(authContext?.authState?.identity?.isAdmin ||
                    authContext?.authState?.identity?.betaUser === "true" ||
                    authContext?.authState?.identity?.adminGameSystems?.length >
                      0) && (
                    <>
                      <Grid item xs={12}>
                        <Divider
                          sx={{
                            mt: 1,
                            mb: 1
                          }}
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <Typography variant="h4" fontWeight="700">
                          Pairing Styles
                        </Typography>
                        <Typography variant="caption" gutterBottom>
                          {`Pairing styles are used to determine how players are paired against each other. This can be changed mid-tournament.`}
                        </Typography>
                      </Grid>

                      <Grid item xs={12}>
                        <GenericPicker
                          name="pairingStyle"
                          label="Pairing Style"
                          value={values.pairingStyle}
                          handleChange={handleChange}
                          list={[
                            "Swiss",
                            "Single Elimination",
                            "Double Elimination"
                          ]}
                          inclAny={false}
                          error={Boolean(
                            touched.pairingStyle && errors.pairingStyle
                          )}
                          helperText={
                            touched.pairingStyle && errors.pairingStyle
                          }
                          disabled={readOnly}
                          endAdornment={
                            <InputAdornment position="end">
                              <BetaPill />
                            </InputAdornment>
                          }
                        />
                      </Grid>
                    </>
                  )}

                  <Grid item xs={12}>
                    <Divider
                      sx={{
                        mt: 1,
                        mb: 1
                      }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Typography variant="h4" fontWeight="700">
                      Scoring Settings
                    </Typography>
                  </Grid>

                  <Grid item xs={12}>
                    <ScorecardPicker
                      name="scorecardId"
                      label="Scorecard"
                      value={values.scorecardId}
                      handleChange={handleChange}
                      scorecards={scorecards}
                      inclAny={false}
                      endAdornment={
                        <InputAdornment position="end">
                          <Tooltip title="Preview Scorecard">
                            <IconButton
                              onClick={() => setScorecardModalOpen(true)}
                            >
                              <BCPEye />
                            </IconButton>
                          </Tooltip>

                          {scorecardDict?.[values.scorecardId]
                            ?.defaultByeGameData?.length && (
                            <Tooltip title="Advanced Scorecard Settings">
                              <IconButton
                                onClick={() => setScorecardAdvancedOpen(true)}
                              >
                                <BCPGear />
                              </IconButton>
                            </Tooltip>
                          )}
                        </InputAdornment>
                      }
                      onClick={async () =>
                        await templateChangesPrompt(values, setFieldValue)
                      }
                      disabled={
                        (!values?.templateModified && templates?.length) ||
                        readOnly
                      }
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Accordion
                      sx={{
                        width: "100%",
                        backgroundColor:
                          theme?.palette?.mode === "dark"
                            ? "background.secondary"
                            : "background.primary"
                      }}
                      disabled={!values.pairingMetrics}
                    >
                      <AccordionSummary
                        expandIcon={
                          <BCPAngleDown
                            style={{
                              "font-size": "14px",
                              width: "14px"
                            }}
                          />
                        }
                        aria-controls="pairingMetrics-content"
                        id="pairingMetrics-header"
                      >
                        <Typography>Pairing Metrics</Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        {values.pairingMetrics && (
                          <FieldArray
                            name="pairingMetrics"
                            render={(arrayHelpers) => (
                              <DragDropContext
                                onDragEnd={(result) => {
                                  onDragEnd(arrayHelpers.move, result);
                                }}
                              >
                                <Droppable
                                  droppableId="pairingMetricsDroppable"
                                  isDropDisabled={Boolean(
                                    event?.gameSystem?.lockMetrics ||
                                      gameSystem?.lockMetrics ||
                                      (!values?.templateModified &&
                                        templates?.length) ||
                                      readOnly
                                  )}
                                >
                                  {(provided, snapshot) => (
                                    <Box
                                      {...provided.droppableProps}
                                      ref={provided.innerRef}
                                      sx={{
                                        width: "100%",
                                        padding: 2,
                                        backgroundColor: snapshot.isDraggingOver
                                          ? "lightBlue"
                                          : "background.secondary"
                                      }}
                                    >
                                      {values.pairingMetrics.map(
                                        (metric, index) => {
                                          return (
                                            <Draggable
                                              key={metric.key}
                                              draggableId={metric.key}
                                              index={index}
                                              isDragDisabled={
                                                event?.gameSystem
                                                  ?.lockMetrics ||
                                                gameSystem?.lockMetrics ||
                                                (!values?.templateModified &&
                                                  templates?.length) ||
                                                readOnly
                                              }
                                            >
                                              {(provided, snapshot) => (
                                                <Box
                                                  ref={provided.innerRef}
                                                  {...provided.draggableProps}
                                                  {...provided.dragHandleProps}
                                                  sx={{
                                                    userSelect: "none",
                                                    padding: 2,
                                                    margin: `0 0 8px 0`,

                                                    // change background colour if dragging
                                                    backgroundColor:
                                                      snapshot.isDragging
                                                        ? "lightgreen"
                                                        : "background.tertiary",
                                                    color: snapshot.isDragging
                                                      ? "#000000"
                                                      : "inherit",

                                                    // styles we need to apply on draggables
                                                    ...provided.draggableProps
                                                      .style
                                                  }}
                                                  onClick={async () =>
                                                    await templateChangesPrompt(
                                                      values,
                                                      setFieldValue
                                                    )
                                                  }
                                                >
                                                  <FormControlLabel
                                                    label={
                                                      <Typography className="MuiFormControlLabel-label">
                                                        {metric.name}{" "}
                                                        {metric.description && (
                                                          <HelpButton
                                                            title={
                                                              metric.description
                                                            }
                                                          />
                                                        )}
                                                      </Typography>
                                                    }
                                                    sx={{
                                                      ml: 0,
                                                      width:
                                                        "calc(100% - 48px)",
                                                      "& .MuiFormControlLabel-label":
                                                        {
                                                          width:
                                                            "calc(100% - 58px)"
                                                        }
                                                    }}
                                                    control={
                                                      <Switch
                                                        color="primary"
                                                        checked={
                                                          values.pairingMetrics[
                                                            index
                                                          ].isOn
                                                        }
                                                        disabled={
                                                          event?.gameSystem
                                                            ?.lockMetrics ||
                                                          gameSystem?.lockMetrics ||
                                                          (!values?.templateModified &&
                                                            templates?.length) ||
                                                          readOnly
                                                        }
                                                        onChange={(e) => {
                                                          setFieldValue(
                                                            `pairingMetrics[${index}].isOn`,
                                                            !values
                                                              .pairingMetrics[
                                                              index
                                                            ].isOn
                                                          );
                                                        }}
                                                      />
                                                    }
                                                    labelPlacement="start"
                                                  />
                                                  <IconButton
                                                    sx={{ ml: "8px" }}
                                                  >
                                                    <BCPGripLines
                                                      style={{
                                                        "font-size": "18px",
                                                        width: "18px"
                                                      }}
                                                    />
                                                  </IconButton>
                                                </Box>
                                              )}
                                            </Draggable>
                                          );
                                        }
                                      )}
                                    </Box>
                                  )}
                                </Droppable>
                              </DragDropContext>
                            )}
                          />
                        )}
                      </AccordionDetails>
                    </Accordion>
                  </Grid>
                  <Grid item xs={12}>
                    <Accordion
                      sx={{
                        width: "100%",
                        backgroundColor:
                          theme?.palette?.mode === "dark"
                            ? "background.secondary"
                            : "background.primary"
                      }}
                      disabled={!values.placingMetrics}
                    >
                      <AccordionSummary
                        expandIcon={
                          <BCPAngleDown
                            style={{
                              "font-size": "14px",
                              width: "14px"
                            }}
                          />
                        }
                        aria-controls="placingMetrics-content"
                        id="placingMetrics-header"
                      >
                        <Typography>Placing Metrics</Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        {values.placingMetrics && (
                          <FieldArray
                            name="placingMetrics"
                            render={(arrayHelpers) => (
                              <DragDropContext
                                onDragEnd={(result) => {
                                  onDragEnd(arrayHelpers.move, result);
                                }}
                              >
                                <Droppable
                                  droppableId="placingMetricsDroppable"
                                  isDropDisabled={Boolean(
                                    event?.gameSystem?.lockMetrics ||
                                      gameSystem?.lockMetrics ||
                                      (!values?.templateModified &&
                                        templates?.length) ||
                                      readOnly
                                  )}
                                >
                                  {(provided, snapshot) => (
                                    <Box
                                      {...provided.droppableProps}
                                      ref={provided.innerRef}
                                      sx={{
                                        width: "100%",
                                        padding: 2,
                                        backgroundColor: snapshot.isDraggingOver
                                          ? "lightBlue"
                                          : "background.secondary"
                                      }}
                                      onClick={async () =>
                                        await templateChangesPrompt(
                                          values,
                                          setFieldValue
                                        )
                                      }
                                    >
                                      {values.placingMetrics.map(
                                        (metric, index) => {
                                          return (
                                            <Draggable
                                              key={metric.key}
                                              draggableId={metric.key}
                                              index={index}
                                              isDragDisabled={
                                                event?.gameSystem
                                                  ?.lockMetrics ||
                                                gameSystem?.lockMetrics ||
                                                (!values?.templateModified &&
                                                  templates?.length) ||
                                                readOnly
                                              }
                                            >
                                              {(provided, snapshot) => (
                                                <Box
                                                  ref={provided.innerRef}
                                                  {...provided.draggableProps}
                                                  {...provided.dragHandleProps}
                                                  sx={{
                                                    userSelect: "none",
                                                    padding: 2,
                                                    margin: `0 0 8px 0`,

                                                    // change background colour if dragging
                                                    backgroundColor:
                                                      snapshot.isDragging
                                                        ? "lightgreen"
                                                        : "background.tertiary",
                                                    color: snapshot.isDragging
                                                      ? "#000000"
                                                      : "inherit",

                                                    // styles we need to apply on draggables
                                                    ...provided.draggableProps
                                                      .style
                                                  }}
                                                >
                                                  <FormControlLabel
                                                    label={
                                                      <Typography className="MuiFormControlLabel-label">
                                                        {metric.name}{" "}
                                                        {metric.description && (
                                                          <HelpButton
                                                            title={
                                                              metric.description
                                                            }
                                                          />
                                                        )}
                                                      </Typography>
                                                    }
                                                    sx={{
                                                      ml: 0,
                                                      width:
                                                        "calc(100% - 48px)",
                                                      "& .MuiFormControlLabel-label":
                                                        {
                                                          width:
                                                            "calc(100% - 58px)"
                                                        }
                                                    }}
                                                    control={
                                                      <Switch
                                                        color="primary"
                                                        checked={
                                                          values.placingMetrics[
                                                            index
                                                          ].isOn
                                                        }
                                                        disabled={
                                                          event?.gameSystem
                                                            ?.lockMetrics ||
                                                          gameSystem?.lockMetrics ||
                                                          (!values?.templateModified &&
                                                            templates?.length) ||
                                                          readOnly
                                                        }
                                                        onChange={(e) => {
                                                          setFieldValue(
                                                            `placingMetrics[${index}].isOn`,
                                                            !values
                                                              .placingMetrics[
                                                              index
                                                            ].isOn
                                                          );
                                                        }}
                                                      />
                                                    }
                                                    labelPlacement="start"
                                                  />
                                                  <IconButton
                                                    sx={{ ml: "8px" }}
                                                  >
                                                    <BCPGripLines
                                                      style={{
                                                        "font-size": "18px",
                                                        width: "18px"
                                                      }}
                                                    />
                                                  </IconButton>
                                                </Box>
                                              )}
                                            </Draggable>
                                          );
                                        }
                                      )}
                                    </Box>
                                  )}
                                </Droppable>
                              </DragDropContext>
                            )}
                          />
                        )}
                      </AccordionDetails>
                    </Accordion>
                  </Grid>

                  {values?.eventType === "Teams Event" && (
                    <Grid item xs={12}>
                      <Accordion
                        sx={{
                          width: "100%",
                          backgroundColor:
                            theme?.palette?.mode === "dark"
                              ? "background.secondary"
                              : "background.primary"
                        }}
                        disabled={!values.placingMetrics}
                      >
                        <AccordionSummary
                          expandIcon={
                            <BCPAngleDown
                              style={{
                                "font-size": "14px",
                                width: "14px"
                              }}
                            />
                          }
                          aria-controls="playerPlacingMetrics-content"
                          id="playerPlacingMetrics-header"
                        >
                          <Typography>Player Placing Metrics</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                          {values.playerPlacingMetrics && (
                            <FieldArray
                              name="playerPlacingMetrics"
                              render={(arrayHelpers) => (
                                <DragDropContext
                                  onDragEnd={(result) => {
                                    onDragEnd(arrayHelpers.move, result);
                                  }}
                                >
                                  <Droppable
                                    droppableId="playerPlacingMetricsDroppable"
                                    isDropDisabled={Boolean(
                                      event?.gameSystem?.lockMetrics ||
                                        gameSystem?.lockMetrics ||
                                        readOnly
                                    )}
                                  >
                                    {(provided, snapshot) => (
                                      <Box
                                        {...provided.droppableProps}
                                        ref={provided.innerRef}
                                        sx={{
                                          width: "100%",
                                          padding: 2,
                                          backgroundColor:
                                            snapshot.isDraggingOver
                                              ? "lightBlue"
                                              : "background.secondary"
                                        }}
                                      >
                                        {values.playerPlacingMetrics.map(
                                          (metric, index) => {
                                            return (
                                              <Draggable
                                                key={metric.key}
                                                draggableId={metric.key}
                                                index={index}
                                                isDragDisabled={
                                                  event?.gameSystem
                                                    ?.lockMetrics ||
                                                  gameSystem?.lockMetrics ||
                                                  readOnly
                                                }
                                              >
                                                {(provided, snapshot) => (
                                                  <Box
                                                    ref={provided.innerRef}
                                                    {...provided.draggableProps}
                                                    {...provided.dragHandleProps}
                                                    sx={{
                                                      userSelect: "none",
                                                      padding: 2,
                                                      margin: `0 0 8px 0`,

                                                      // change background colour if dragging
                                                      backgroundColor:
                                                        snapshot.isDragging
                                                          ? "lightgreen"
                                                          : "background.tertiary",
                                                      color: snapshot.isDragging
                                                        ? "#000000"
                                                        : "inherit",

                                                      // styles we need to apply on draggables
                                                      ...provided.draggableProps
                                                        .style
                                                    }}
                                                  >
                                                    <FormControlLabel
                                                      label={
                                                        <Typography className="MuiFormControlLabel-label">
                                                          {metric.name}{" "}
                                                          {metric.description && (
                                                            <HelpButton
                                                              title={
                                                                metric.description
                                                              }
                                                            />
                                                          )}
                                                        </Typography>
                                                      }
                                                      sx={{
                                                        ml: 0,
                                                        width:
                                                          "calc(100% - 48px)",
                                                        "& .MuiFormControlLabel-label":
                                                          {
                                                            width:
                                                              "calc(100% - 58px)"
                                                          }
                                                      }}
                                                      control={
                                                        <Switch
                                                          color="primary"
                                                          checked={
                                                            values
                                                              .playerPlacingMetrics[
                                                              index
                                                            ].isOn
                                                          }
                                                          disabled={
                                                            event?.gameSystem
                                                              ?.lockMetrics ||
                                                            gameSystem?.lockMetrics ||
                                                            readOnly
                                                          }
                                                          onChange={(e) => {
                                                            setFieldValue(
                                                              `playerPlacingMetrics[${index}].isOn`,
                                                              !values
                                                                .playerPlacingMetrics[
                                                                index
                                                              ].isOn
                                                            );
                                                          }}
                                                        />
                                                      }
                                                      labelPlacement="start"
                                                    />
                                                    <IconButton
                                                      sx={{ ml: "8px" }}
                                                    >
                                                      <BCPGripLines
                                                        style={{
                                                          "font-size": "18px",
                                                          width: "18px"
                                                        }}
                                                      />
                                                    </IconButton>
                                                  </Box>
                                                )}
                                              </Draggable>
                                            );
                                          }
                                        )}
                                      </Box>
                                    )}
                                  </Droppable>
                                </DragDropContext>
                              )}
                            />
                          )}
                        </AccordionDetails>
                      </Accordion>
                    </Grid>
                  )}

                  {(authContext?.authState?.identity?.betaUser === "true" ||
                    authContext?.authState?.identity?.isAdmin ||
                    authContext?.authState?.identity?.adminGameSystems?.length >
                      0) &&
                    placingRecordTypes?.length > 1 && (
                      <Grid item xs={12}>
                        <GenericPicker
                          label="Placing Record Type"
                          name="placingRecordType.key"
                          inclAny={false}
                          list={placingRecordTypes.map((el) => {
                            return { value: el.key, label: el.name };
                          })}
                          value={values.placingRecordType?.key || ""}
                          handleChange={(e) => {
                            setFieldValue("placingRecordType", {
                              key: e.target.value,
                              name: placingRecordTypes.filter(
                                (el) => el.key === e.target.value
                              )[0]?.name
                            });
                          }}
                          endAdornment={
                            <InputAdornment position="end">
                              <BetaPill />
                            </InputAdornment>
                          }
                        />
                      </Grid>
                    )}
                </>
              )}

              <Grid item xs={12}>
                <Button fullWidth variant="contained" type="submit">
                  {eventId ? "Save Changes" : "Next: Registration & Tickets"}
                </Button>
              </Grid>

              {/* TODO: Fix scorecardc modal not opening */}
              {scorecardAdvancedOpen && (
                <ScorecardAdvancedModal
                  open={scorecardAdvancedOpen}
                  setOpen={setScorecardAdvancedOpen}
                  scorecard={scorecardDict?.[values.scorecardId]}
                  setFieldValue={setFieldValue}
                  values={values}
                  disabled={readOnly}
                />
              )}

              <TemplateModal
                template={template}
                open={tempModalOpen}
                setOpen={setTempModalOpen}
              />

              {scorecardModalOpen && (
                <ScorecardModal
                  open={scorecardModalOpen}
                  setOpen={setScorecardModalOpen}
                  scorecardId={values?.scorecardId}
                  event={eventValues || values}
                  gameSystem={gameSystem}
                />
              )}
            </Grid>
          </form>
        )}
      </Formik>
    </Grid>
  );
}
